import { buildSminexClubSearchSubscription, SminexClubSearchPayload } from './subscription';
import { ensureValidAccessToken } from '@shared/api/ws-auth';
import { getJWTTokens } from '@shared/libs/local-storage';
import { mapSminexClubSearchPayload } from './mapPayload';
import { getWsEndpoint } from '@shared/api/api-url';
import { createClient, Client } from 'graphql-ws';
import {
  SminexClubSearchClient,
  SminexClubSearchFilters,
  SminexClubSearchListener,
  SminexClubSearchState,
  SminexClubSearchStatus,
} from './types';

export const SMINEX_CLUB_SEARCH_PAGE_SIZE = 24;

let clients: SminexClubSearchClient[] = [];
let status: SminexClubSearchStatus = 'idle';
let hasMore = false;
let isLoadingMore = false;
let loadMoreError = false;
let baseFilters: SminexClubSearchFilters | null = null;
let currentPage = 1;
let client: Client | null = null;
let unsubscribe: (() => void) | null = null;
let generation = 0;

const listeners = new Set<SminexClubSearchListener>();

const getState = (): SminexClubSearchState => ({ clients, status, hasMore, isLoadingMore, loadMoreError });

const notify = () => {
  const state = getState();
  listeners.forEach((listener) => listener(state));
};

const setStatus = (next: SminexClubSearchStatus) => {
  if (status === next) return;
  status = next;
  notify();
};

const setLoadingMore = (next: boolean) => {
  if (isLoadingMore === next) return;
  isLoadingMore = next;
  notify();
};

const setLoadMoreError = (next: boolean) => {
  if (loadMoreError === next) return;
  loadMoreError = next;
  notify();
};

const handleConnectError = (page: number) => {
  setLoadingMore(false);

  if (page === 1) {
    setStatus('error');
  } else {
    setLoadMoreError(true);
  }
};

const applyClientsPage = (page: number, next: SminexClubSearchClient[], receivedCount: number) => {
  const existingIds = new Set(clients.map((item) => item.crmUuid));
  const deduped = page === 1 ? next : next.filter((item) => !existingIds.has(item.crmUuid));

  clients = page === 1 ? next : [...clients, ...deduped];
  hasMore = receivedCount >= SMINEX_CLUB_SEARCH_PAGE_SIZE;
  currentPage = page;
  status = 'ready';
  isLoadingMore = false;
  notify();
};

const disposeClient = () => {
  unsubscribe?.();
  unsubscribe = null;

  if (!client) return;

  const activeClient = client;
  client = null;
  void Promise.resolve(activeClient.dispose()).catch(() => {});
};

const connect = async (currentGeneration: number, filters: SminexClubSearchFilters, page: number) => {
  if (currentGeneration !== generation) return;

  disposeClient();
  setLoadMoreError(false);

  if (page === 1) {
    setStatus('connecting');
  } else {
    setLoadingMore(true);
  }

  const accessToken = await ensureValidAccessToken();
  if (currentGeneration !== generation) return;

  if (!accessToken) {
    handleConnectError(page);
    return;
  }

  const wsUrl = getWsEndpoint();
  let receivedClients = false;

  try {
    client = createClient({
      url: wsUrl,
      lazy: false,
      retryAttempts: 0,
      connectionParams: () => {
        const { accessToken: token } = getJWTTokens();
        return {
          Authorization: `Bearer ${token}`,
        };
      },
    });

    unsubscribe = client.subscribe(
      { query: buildSminexClubSearchSubscription(filters, { page, pageSize: SMINEX_CLUB_SEARCH_PAGE_SIZE }) },
      {
        next: (result) => {
          if (currentGeneration !== generation) return;

          if (result.errors?.length) {
            disposeClient();
            handleConnectError(page);
            return;
          }

          const mapped = mapSminexClubSearchPayload(result.data as SminexClubSearchPayload | undefined);
          if (!mapped) return;

          receivedClients = true;
          applyClientsPage(page, mapped.clients, mapped.receivedCount);
          disposeClient();
        },
        error: () => {
          if (currentGeneration !== generation || receivedClients) return;
          disposeClient();
          handleConnectError(page);
        },
        complete: () => {
          if (currentGeneration !== generation || receivedClients) return;
          disposeClient();
          handleConnectError(page);
        },
      }
    );
  } catch {
    disposeClient();
    handleConnectError(page);
  }
};

export const getSearchClients = (): SminexClubSearchClient[] => clients;

export const getSearchStatus = (): SminexClubSearchStatus => status;

export const getSearchHasMore = (): boolean => hasMore;

export const getSearchIsLoadingMore = (): boolean => isLoadingMore;

export const getSearchLoadMoreError = (): boolean => loadMoreError;

export const subscribeSearch = (listener: SminexClubSearchListener): (() => void) => {
  listeners.add(listener);
  listener(getState());
  return () => {
    listeners.delete(listener);
  };
};

export const searchSminexClubClients = (filters: SminexClubSearchFilters) => {
  generation += 1;
  const currentGeneration = generation;
  baseFilters = filters;
  currentPage = 1;
  hasMore = false;
  void connect(currentGeneration, filters, 1);
};

const startLoadMore = () => {
  if (!baseFilters || isLoadingMore || !hasMore || status !== 'ready') return;

  generation += 1;
  const currentGeneration = generation;
  const nextPage = currentPage + 1;
  void connect(currentGeneration, baseFilters, nextPage);
};

export const loadMoreSminexClubClients = () => {
  if (loadMoreError) return;
  startLoadMore();
};

export const retrySminexClubLoadMore = () => {
  loadMoreError = false;
  startLoadMore();
};

export const closeSearchConnection = () => {
  generation += 1;
  disposeClient();
  isLoadingMore = false;
  loadMoreError = false;
  if (status === 'connecting' || status === 'error') {
    setStatus(clients.length ? 'ready' : 'idle');
  } else {
    notify();
  }
};

export const clearSminexClubSearch = () => {
  generation += 1;
  disposeClient();
  clients = [];
  status = 'idle';
  hasMore = false;
  isLoadingMore = false;
  loadMoreError = false;
  baseFilters = null;
  currentPage = 1;
  notify();
};
